import { atom, selector, selectorFamily } from 'recoil'

import { MESSAGE_LIST_TYPE_NOT_ARCHIVED, STORE_KEY_ID_LIST } from 'Constants/'
import { statisticsApiGetMessageTotalCounters } from 'Api/StatisticsApi'
import { beMessApiGenerateThumbnail } from 'Api/BeMessApi'
import { logAppError } from 'Helpers/logger'
import getIntl from 'Helpers/intl'

import { pageErrorState } from './page'
import { storeGet } from 'Helpers/localStorage'

export const resultsState = atom({
  key: 'messageListResults',
  default: {
    items: [],
    totalItems: 0,
    searchParams: {},
    messagesWithForm: []
  }
})

export const resultsMessageHasThumb = selectorFamily({
  key: 'messageListResultThumbByMessageId',
  get: id => async ({ get }) => {
    const item = get(resultsState).items.find(item => item.Id === id)

    if (!item.HasThumbnail) {
      try {
        await beMessApiGenerateThumbnail(item.Id)
      } catch (error) {
        logAppError({ appError: error })
        throw error
      }
    }

    return true
  }
})

export const resultsStateSearchParams = selector({
  key: 'messageListResultsStateSearchParams',
  get: ({ get }) => get(resultsState).searchParams,
  set: ({ set }, searchParams) =>
    set(resultsState, prev => ({
      ...prev,
      searchParams
    }))
})

const evalSearchHasParams = searchParams => {
  return Object.entries(searchParams).reduce((r, [key, val]) => {
    if (['pageNumber', 'pageSize', 'orderBy'].includes(key)) {
      return r
    }

    if (key === 'type' && val === MESSAGE_LIST_TYPE_NOT_ARCHIVED) {
      return r
    }

    if (key === 'filterByField' && val === 'LastUpdated') {
      return r
    }

    if (key === 'tags' && val && val.length) {
      return true
    }

    if (key !== 'tags' && val) {
      return true
    }

    return r || false
  }, false)
}

export const resultsStateHasSearchParams = selector({
  key: 'messageListResultsStateHasSearchParams',
  get: ({ get }) => {
    const { searchParams } = get(resultsState)

    return evalSearchHasParams(searchParams)
  }
})

export const resultsStateEmptyState = selector({
  key: 'messageListResultsStateEmptyState',
  get: ({ get }) => {
    const { items, searchParams } = get(resultsState)
    const hasSearchParams = evalSearchHasParams(searchParams)
    return !hasSearchParams && !items.length
  }
})

export const selectedResultsState = atom({
  key: 'messageListSelectedResultsState',
  default: []
})

export const selectedResultsStateAllSelected = selector({
  key: 'messageListSelectedResultsStateAllSelected',
  get: ({ get }) => {
    const selectedIds = get(selectedResultsState)
    const resultsIds = get(resultsState).items.map(item => item.Id)

    return resultsIds.every(id => selectedIds.includes(id))
  },
  set: ({ set, get, reset }, val) => {
    const resultsIds = get(resultsState).items.map(item => item.Id)
    const selectedIds = get(selectedResultsState)
    val
      ? set(
          selectedResultsState,
          Array.from(new Set([...selectedIds, ...resultsIds]))
        )
      : reset(selectedResultsState)
  }
})

export const selectedResultsStateById = selectorFamily({
  key: 'selectedResultsStateById',
  get: idMessage => ({ get }) => get(selectedResultsState).includes(idMessage),
  set: idMessage => ({ get, set }, val) => {
    const selectedIds = get(selectedResultsState)
    if (val && selectedIds.includes(idMessage)) {
      return
    }
    val
      ? set(selectedResultsState, [...selectedIds, idMessage])
      : set(
          selectedResultsState,
          selectedIds.filter(id => id !== idMessage)
        )
  }
})

export const messageSentCountState = selectorFamily({
  key: 'messageListMessageSentCountState',
  get: idMessage => async ({ set }) => {
    try {
      const idList = parseInt(storeGet(STORE_KEY_ID_LIST))
      const result = await statisticsApiGetMessageTotalCounters(
        idList,
        idMessage,
        'EI'
      )
      return result.Counters.TotalCounters.EI
    } catch (error) {
      logAppError({ appError: error })
      set(pageErrorState, {
        error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
        severity: 'danger'
      })
    }
  }
})

export const collaborationState = atom({
  key: 'messageListCollaborationState',
  default: []
})

export const messagesCollaborationState = atom({
  key: 'messageListMessagesCollaborationState',
  default: Promise.resolve({
    Items: []
  })
})

export const isMessageUsed = selectorFamily({
  key: 'messageListGetUsedMessages',
  get: id => ({ get }) => {
    const { messagesWithForm } = get(resultsState)
    return messagesWithForm.includes(id)
  }
})
